import { useEffect, useRef, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/lib/auth-store'
import { apiFetch, ApiClientError } from '@/lib/api-client'
import type { SessionUser } from '@/lib/session-types'

interface Props {
  children: ReactNode
}

/**
 * Auth guard for protected routes.
 *
 * On mount, if a JWT exists, refresh the session user from /auth/me so the
 * sidebar reflects current org/artist memberships even if the persisted
 * `user` shape from localStorage is stale (e.g. after a backend deploy that
 * extended the SessionUser shape, or after the user accepted a new
 * artist membership in another tab).
 *
 * Done once per mount — not on every navigation.
 */
export function RequireAuth({ children }: Props) {
  const accessToken = useAuthStore((s) => s.accessToken)
  const setUser = useAuthStore((s) => s.setUser)
  const logout = useAuthStore((s) => s.logout)
  const location = useLocation()
  const refreshed = useRef(false)

  useEffect(() => {
    if (!accessToken || refreshed.current) return
    refreshed.current = true
    apiFetch<SessionUser>('/auth/me')
      .then((me) => setUser(me))
      .catch((err: unknown) => {
        // 401 means the token has been invalidated server-side — log out.
        if (err instanceof ApiClientError && err.status === 401) {
          logout()
        }
      })
  }, [accessToken, setUser, logout])

  if (!accessToken) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />
  }
  return <>{children}</>
}
